//may11 AuthContext.jsx
import React, { createContext, useContext, useEffect, useReducer, useMemo, useRef, useState, useCallback } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useLoading } from './LoadingContext'; // Use LoadingContext
//import { debounce } from 'lodash'; // Import debounce from lodash

// Create AuthContext
const AuthContext = createContext();

// Custom hook to access the context
export function useAuth() {
    return useContext(AuthContext);
}

// Initial state for the auth reducer
const initialState = {
    currentUser: undefined,  // undefined means "auth is loading"
    userPermissions: [],
    userRole: null,
    userProfile: null,
    organizationId: null, // Add organizationId to the state
    accountInGoodStanding: undefined,
    loading: true, // Track whether authentication is still loading
};

// Detect if we’re in public preview mode
// NOTE: supports BOTH legacy "true" string and storageUtils wrapper { value: true, timestamp }
const readIsPublicPreview = () => {
  try {
    if (typeof window === "undefined") return false;

    const raw = sessionStorage.getItem("isPublicPreview");

    // legacy compatibility
    if (raw === "true") return true;

    // storageUtils wrapper compatibility
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && parsed.value === true) return true;
      } catch (_) {
        // ignore parse failure; fall through
      }
    }

    return false;
  } catch (_) {
    return false;
  }
};

// Detect if we’re in the public embed route.
//
// ENTERPRISE:
// /embed/forms/:formId is intentionally unauthenticated.
// It should resolve as an anonymous public runtime without initializing
// Firebase Auth, fetching account/org profile data, or leaking logged-in app
// context into the iframe.
const readIsPublicEmbedRoute = () => {
  try {
    if (typeof window === "undefined") return false;
    return window.location.pathname.startsWith("/embed/forms/");
  } catch (_) {
    return false;
  }
};

const shouldBypassAuthRuntime = () => {
  return readIsPublicPreview() || readIsPublicEmbedRoute();
};


// Helper function for shallow comparison
const shallowCompare = (obj1, obj2) => {
    if (obj1 === obj2) return true; // Direct reference comparison
    if (typeof obj1 !== "object" || typeof obj2 !== "object") return false; // Non-objects
    for (let key in obj1) {
        if (obj1[key] !== obj2[key]) return false;
    }
    return true;
};

// Reducer to manage the auth state
const authReducer = (state, action) => {
//    console.log(`[AuthContext] authReducer called. Action type: ${action.type}`);
//    console.log("[AuthContext] Previous state:", state);

    switch (action.type) {
        case 'SET_USER': {
        const { currentUser, userPermissions, userRole, userProfile, organizationId, subscriptionStatus, accountInGoodStanding } = action.payload; 

            // Perform shallow comparison instead of deep (optimize for shallow changes)
            const isSameUser =
                state.currentUser === currentUser &&
                shallowCompare(state.userPermissions, userPermissions) &&
                state.userRole === userRole &&
                state.organizationId === organizationId &&
                shallowCompare(state.userProfile || {}, userProfile || {});

            if (isSameUser) {
//                console.log("[AuthContext] No state change detected, skipping SET_USER update.");
                return state;
            }

//            console.log("[AuthContext] Setting user with payload:", action.payload);
            const updatedState = {
                ...state,
                currentUser,
                userPermissions,
                userRole,
                organizationId,
                userProfile,
                subscriptionStatus,
                accountInGoodStanding,
                loading: false // No longer loading after user is set
            };
            return updatedState;
        }


        case 'RESET_USER': {
            if (state.currentUser === null && !state.loading) {
//                console.log("[AuthContext] No state change detected, skipping RESET_USER update.");
                return state;
            }

//            console.log("[AuthContext] Resetting user state to initial:", initialState);
            return {
                ...initialState,
                currentUser: null, // Explicitly setting currentUser to null
                loading: false // No longer loading once the user is reset
            };
        }

        case 'START_LOADING': {
            if (state.loading) {
//                console.log("[AuthContext] Loading state already true, skipping update.");
                return state;
            }
//           console.log("[AuthContext] Setting loading to true");
            return {
                ...state,
                loading: true // Mark state as loading
            };
        }

        default: {
            console.warn("[AuthContext] Unknown action type:", action.type);
            return state;
        }
    }
};

// AuthProvider component manages authentication state
export function AuthProvider({ children }) {
    const [state, dispatch] = useReducer(authReducer, initialState);
    const [initialized, setInitialized] = useState(false); // Track initialization state
    const [authLoading, setAuthLoading] = useState(true); // Track Firebase authentication process
//    const [accountInGoodStanding, setAccountInGoodStanding] = useState(false); 
    const { setLoading } = useLoading(); // Use LoadingContext
    const isMounted = useRef(true); // Ensure state is only set when mounted
    const isAuthLoadingTracked = useRef(false); // Track whether 'Auth' is already tracked in loading context
const authListenerInitialized = useRef(false); // Prevent multiple listeners

    // Cleanup for when the component unmounts
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);
    
// Check sessionStorage when app loads
useEffect(() => {
    // Public preview/embed routes intentionally bypass Firebase Auth.
    // Resolve React auth context as anonymous for this public runtime.
    //
    // IMPORTANT:
    // RESET_USER here does NOT call Firebase signOut().
    // It only sets currentUser:null and loading:false in React context.
    if (shouldBypassAuthRuntime()) {
        dispatch({ type: 'RESET_USER' });
        setInitialized(true);
        setAuthLoading(false);
        return;
    }

    // Prevent running if Firebase is still restoring the session
    if (authLoading) return;

    const uid = auth.currentUser?.uid;

    //  If no Firebase user, we can safely reset.
    if (!uid) {
//        console.warn("[AuthContext] No authenticated user. Resetting authentication state.");
        dispatch({ type: 'RESET_USER' });
        setInitialized(true); // auth resolved; user is signed out
        return;
    }

    //  If a Firebase user exists, do NOT reset user just because cache is empty.
    // Cache miss is normal on first login / incognito. Auth listener will fetch profile.
    const cachedProfile = getUserProfileFromCache(uid);

    if (!cachedProfile) {
//        console.log("[AuthContext] No cached profile yet; leaving auth state intact and allowing profile fetch.");
        setInitialized(false); // ok: “initialized” can remain false while profile fetch happens
        return;               //  DO NOT dispatch RESET_USER here
    }

//    console.log(`[AuthContext] Cached profile found for UID: ${uid}. Setting initialized to true.`);
    setInitialized(true);
}, [authLoading]); // Dependency ensures it only runs *after* Firebase auth resolves



// Centralized cache functions for userProfile and accountInGoodStanding
const storeInCache = (key, data) => {
//    console.log(`[AuthContext: storeInCache] Storing data for key ${key}:`, data);
    sessionStorage.setItem(key, JSON.stringify(data));
};

const getFromCache = (key) => {
//    console.log(`[AuthContext: getFromCache] Checking sessionStorage for key: ${key}`);
    const cachedData = sessionStorage.getItem(key);
//    console.log(`[AuthContext: getFromCache] SessionStorage for key ${key}:`, cachedData ? 'found' : 'not found');
    return cachedData ? JSON.parse(cachedData) : null;
};

const clearFromCache = (key) => {
//    console.log(`[AuthContext: clearFromCache] Clearing cached data for key ${key}`);
    sessionStorage.removeItem(key);
};

// Usage of these functions in your useEffect or state updates

// For caching user profile:
const storeUserProfileInCache = (uid, userProfile) => {
    if (!uid || !userProfile) {
        console.error(`[AuthContext: storeUserProfileInCache] ERROR: Cannot cache profile. UID or userProfile is missing.`);
        return;
    }

    // Construct a consistent profile structure
    const profileToStore = {
        organizationId: userProfile.organizationId || null,  // Ensure it is never undefined
        subscriptionStatus: userProfile.subscriptionStatus || "unknown",  // Default to "unknown"
        hasValidPaymentMethod: userProfile.hasValidPaymentMethod ?? false,  // Default to false
        permissions: userProfile.permissions || [], // Ensure permissions are never undefined
        isOwner: userProfile.isOwner || false, // Default to false if missing
        planName: userProfile.planName || "N/A", // Default plan name
        renewalDate: userProfile.renewalDate || null, // Keep renewalDate explicit
        trialEndsAt: userProfile.trialEndsAt || null, // Keep trial expiry explicit
        email: userProfile.email || "unknown", // Ensure email is never undefined
        role: userProfile.role || "unknown", // Ensure role is never undefined
        ...userProfile // Merge any additional fields
    };

    // Log before storing
//    console.log(`[AuthContext: storeUserProfileInCache] Storing user profile for UID ${uid}:`, profileToStore);

    // Warnings if key values are missing
    if (!profileToStore.organizationId) {
        console.warn(`[AuthContext: storeUserProfileInCache] WARNING: organizationId is missing for UID ${uid}`);
    }
    if (!profileToStore.subscriptionStatus || profileToStore.subscriptionStatus === "unknown") {
        console.warn(`[AuthContext: storeUserProfileInCache] WARNING: subscriptionStatus is missing or unknown for UID ${uid}`);
    }

    // Store in session storage
    storeInCache(`userProfile_${uid}`, profileToStore);
};


// For retrieving user profile:
const getUserProfileFromCache = (uid) => {
//    console.log(`[AuthContext: getUserProfileFromCache] Retrieving user profile for UID ${uid} from cache.`);
    const profile = getFromCache(`userProfile_${uid}`);

    if (!profile) {
        console.warn(`[AuthContext: getUserProfileFromCache] No cached profile found for UID ${uid}`);
        return null;
    }

//    console.log(`[AuthContext: getUserProfileFromCache] Cached profile for UID ${uid}:`, {
//        organizationId: profile.organizationId,
//        subscriptionStatus: profile.subscriptionStatus,
 //       hasValidPaymentMethod: profile.hasValidPaymentMethod
//    });

    return profile;
};

// For clearing user profile and accountInGoodStanding from cache:
const clearUserProfileCache = (uid) => {
//    console.log(`[AuthContext: clearUserProfileCache] Clearing user profile and account standing cache for UID ${uid}`);
    
    // Clear user profile cache
    clearFromCache(`userProfile_${uid}`);

    // Clear account standing cache
    clearFromCache(`accountInGoodStanding_${uid}`);

//    console.log(`[AuthContext: clearUserProfileCache] Cleared both userProfile and accountInGoodStanding for UID ${uid}`);
};

// For caching accountInGoodStanding:
const storeAccountStandingInCache = (uid, isInGoodStanding, subscriptionStatus) => {
//    console.log(`[AuthContext: storeAccountStandingInCache] Storing account standing for UID ${uid}:`, {
//        isInGoodStanding,
//        subscriptionStatus
//    });
    storeInCache(`accountInGoodStanding_${uid}`, isInGoodStanding);
};

// For retrieving accountInGoodStanding:
const getAccountStandingFromCache = (uid) => {
//    console.log(`[AuthContext: getAccountStandingFromCache] Checking sessionStorage for accountInGoodStanding for UID ${uid}`);
    const standing = getFromCache(`accountInGoodStanding_${uid}`);
//    console.log(`[AuthContext: getAccountStandingFromCache] Cached accountInGoodStanding for UID ${uid}:`, standing !== null ? standing : 'not found');
    return standing;
};


// Store active fetch requests to prevent duplicate Firestore calls
const activeFetchRequestsRef = useRef(new Map());

const fetchUserProfile = async (uid, retries = 5, delay = 1000) => {
    try {
//        console.log(`[AuthContext: fetchUserProfile] Checking sessionStorage for UID: ${uid}`);

        // Check if a fetch request is already active for this UID
        if (activeFetchRequestsRef.current.has(uid)) {
//            console.warn(`[AuthContext: fetchUserProfile] Fetch already in progress for UID: ${uid}, awaiting existing request.`);
            return await activeFetchRequestsRef.current.get(uid); // Return the existing promise
        }

        // Create a new promise for this request and store it
        const fetchPromise = (async () => {
            let userSnap;
            let attempts = 0;

            // Check if the profile is cached & ensure necessary fields exist
            const cachedProfile = getFromCache(`userProfile_${uid}`);
            if (cachedProfile && cachedProfile.organizationId && cachedProfile.subscriptionStatus !== undefined) {
//                console.log(`[AuthContext: fetchUserProfile] Returning cached profile for UID: ${uid}`);
                const accountInGoodStanding = getAccountStandingFromCache(uid);
//                console.log(`[AuthContext: fetchUserProfile] Found cached accountInGoodStanding for UID: ${uid}:`, accountInGoodStanding);
                return { userProfile: cachedProfile, accountInGoodStanding };
            } else {
//                console.warn(`[AuthContext: fetchUserProfile] Cached profile for UID: ${uid} is missing required fields. Fetching fresh data.`);
            }

//            console.log(`[AuthContext: fetchUserProfile] No valid cached profile found for UID: ${uid}. Fetching Firestore profile...`);

            // Fetch User Profile from Accounts Collection with retries
            while (attempts < retries) {
                const userRef = doc(db, 'accounts', uid);
                userSnap = await getDoc(userRef);

                if (userSnap.exists()) {
//                    console.log(`[AuthContext: fetchUserProfile] Firestore profile found on attempt ${attempts + 1}`);
                    break;
                }

                attempts++;
                const currentDelay = delay * Math.pow(2, attempts - 1);
                console.warn(`[AuthContext: fetchUserProfile] Profile not found for UID: ${uid}. Retrying (${attempts}/${retries}) after ${currentDelay}ms...`);
                await new Promise((resolve) => setTimeout(resolve, currentDelay));
            }

            if (!userSnap || !userSnap.exists()) {
//                console.error(`[AuthContext: fetchUserProfile] Profile not found after ${retries} retries for UID: ${uid}`);

                // 🔎 DEBUG: This is the classic signup timing failure (accounts/{uid} not written yet)
                console.error("[AuthContext][DEBUG] fetchUserProfile FAILED after retries:", {
                  uid,
                  retries,
                });

                throw new Error("User profile not found in Firestore");
            }


            const userProfile = userSnap.data();
            let { role, organizationId } = userProfile;
//            console.log(`[AuthContext: fetchUserProfile] Extracted user profile:`, userProfile);

            // Attempt to fetch missing organizationId if not present
            if (!organizationId) {
                console.warn(`[AuthContext: fetchUserProfile] organizationId is missing from user profile!`);
            }

            // Fetch Organization Details
            let organizationData = null;
            let subscriptionStatus = "unknown";
            let hasValidPaymentMethod = false;
            let planName = "N/A";
            let renewalDate = null;
            let trialEndsAt = null;
            let isOwner = false;

            if (organizationId) {
//                console.log(`[AuthContext: fetchUserProfile] Fetching organization data for organizationId: ${organizationId}`);
                const orgRef = doc(db, 'organizations', organizationId);
                const orgSnap = await getDoc(orgRef);

                if (orgSnap.exists()) {
                    organizationData = orgSnap.data();
//                    console.log(`[AuthContext: fetchUserProfile] Organization data found:`, organizationData);

                    // Extract relevant fields
                    const { 
                        ownerId, 
                        subscriptionStatus: orgSubscriptionStatus,
                        hasValidPaymentMethod: orgHasValidPaymentMethod,
                        planName: orgPlanName,
                        renewalDate: orgRenewalDate,
                        trialEndsAt: orgTrialEndsAt
                    } = organizationData;

                    subscriptionStatus = orgSubscriptionStatus || "unknown";
                    hasValidPaymentMethod = orgHasValidPaymentMethod || false;
                    planName = orgPlanName || "N/A";
                    renewalDate = orgRenewalDate || null;
                    trialEndsAt = orgTrialEndsAt || null;

                    isOwner = ownerId === uid;
//                    console.log(`[AuthContext: fetchUserProfile] Is user the owner? ${isOwner}`);
                } else {
//                    console.warn(`[AuthContext: fetchUserProfile] Organization with ID ${organizationId} not found.`);
                }
            } else {
                console.warn(`[AuthContext: fetchUserProfile] No organizationId found for UID: ${uid}.`);
            }

            // 🔹 **Ensure subscriptionStatus is valid (fallback to organization subscriptionStatus)**
            if (!subscriptionStatus || subscriptionStatus === "unknown") {
                console.warn(`[AuthContext: fetchUserProfile] Missing subscriptionStatus for UID ${uid}, checking Firestore...`);
                subscriptionStatus = organizationData?.subscriptionStatus || "unknown";
            }

            // Fetch Role Permissions
            let permissions = [];
            if (role) {
//                console.log(`[AuthContext: fetchUserProfile] Fetching role data for role: ${role}`);
                const roleRef = doc(db, 'roles', role);
                const roleSnap = await getDoc(roleRef);

                if (roleSnap.exists()) {
                    permissions = roleSnap.data().permissions || [];
//                    console.log(`[AuthContext: fetchUserProfile] Permissions for role ${role}:`, permissions);
                } else {
                    console.error(`[AuthContext: fetchUserProfile] Role document not found for role: ${role}`);
                }
            }

            if (isOwner || role === 'billingManager') {
//                console.log(`[AuthContext: fetchUserProfile] Adding billing permissions.`);
                permissions.push('manageBilling', 'viewInvoices');
            }

            // Final Profile Data
            const enhancedProfileData = {
                ...userProfile,
                permissions,
                isOwner,
                organizationId,
                subscriptionStatus,
                hasValidPaymentMethod,
                planName,
                renewalDate,
                trialEndsAt,
            };

//            console.log(`[AuthContext: fetchUserProfile] Enhanced user profile data:`, enhancedProfileData);

            // Store Profile in Cache
            storeUserProfileInCache(uid, enhancedProfileData);

            // **Account Standing Calculation**
            // Ensure trialEndsAt is properly converted to a Date object
            if (typeof trialEndsAt === "string") {
                trialEndsAt = new Date(trialEndsAt);  // Convert string to Date
            } else if (trialEndsAt?.seconds) {
                trialEndsAt = new Date(trialEndsAt.seconds * 1000);  // Convert Firestore Timestamp
            }

            // 🔹 **Account-standing calculation (revised 2025-06-17)**
            //  • active  ➜ always good
            //  • trialing ➜ good while trial valid
            //  • past_due / unpaid / incomplete ➜ need a *valid* PM
            //  • everything else (canceled, paused, etc.) ➜ not good
            const needsPayment = [
              "past_due",
              "unpaid",
              "incomplete",
              "incomplete_expired",
            ].includes(subscriptionStatus);

            let isInGoodStanding;

            if (subscriptionStatus === "trialing") {
              const trialIsValid =
                !!trialEndsAt && new Date(trialEndsAt) > new Date();   // still within trial
              isInGoodStanding = trialIsValid;
            } else if (needsPayment) {
              isInGoodStanding = hasValidPaymentMethod;                // let Stripe retry
            } else if (subscriptionStatus === "active") {
              isInGoodStanding = true;                                 // always allow
            } else {
              isInGoodStanding = false;                                // canceled / paused …
            }

            // Store Account Standing in Cache
            storeAccountStandingInCache(uid, isInGoodStanding, subscriptionStatus);

            return { userProfile: enhancedProfileData, accountInGoodStanding: isInGoodStanding };
        })();

        // Store and await the promise
        activeFetchRequestsRef.current.set(uid, fetchPromise);
        const result = await fetchPromise;

        // Cleanup after resolving
        activeFetchRequestsRef.current.delete(uid);
        return result;

    } catch (error) {
        console.error("[AuthContext: fetchUserProfile] Error:", error.message);
        activeFetchRequestsRef.current.delete(uid); // Ensure failed requests are not tracked
        throw error;
    }
};

const setAuthState = useCallback(async (firebaseUser, options = {}) => {
//    console.log("[AuthContext] setAuthState triggered with firebaseUser:", firebaseUser);
    // 🔎 DEBUG: Confirm we got a real Firebase user object (should have uid + getIdToken)
    try {
//      console.log("[AuthContext][DEBUG] setAuthState called:", {
//        hasArg: !!firebaseUser,
//        argKeys: firebaseUser && typeof firebaseUser === "object" ? Object.keys(firebaseUser) : null,
//        uid: firebaseUser?.uid,
//        hasUid: !!firebaseUser?.uid,
//        hasGetIdToken: typeof firebaseUser?.getIdToken === "function",
//      });
    } catch (e) {
      console.warn("[AuthContext][DEBUG] setAuthState logging failed:", e?.message || e);
    }

    // 🛑 No user? Handle logout process
    if (!firebaseUser) {
//        console.log("[AuthContext] No firebaseUser, performing logout.");

        // Clear user profile cache if there was a previous user
        if (state.currentUser?.uid) {
            clearUserProfileCache(state.currentUser.uid);
            clearFromCache(`accountInGoodStanding_${state.currentUser.uid}`); // Clear account standing cache
        }
        
        // Reset authentication state
        dispatch({ type: 'RESET_USER' });
        setAuthLoading(false); // Ensure loading is reset after logout
        return; // Early exit
    }

    // ✅ Avoid redundant updates if the user is already set
    const forceRefresh = options?.forceRefresh === true;

    if (!forceRefresh && state.currentUser?.uid === firebaseUser.uid) {
//        console.log("[AuthContext] User is already set, skipping update.");
        return;
    }


    try {
        // 🟢 Step 1: Attempt to get profile from cache
//        console.log(`[AuthContext] Checking sessionStorage for cached profile of UID: ${firebaseUser.uid}`);
        
        let userProfile = forceRefresh ? null : getUserProfileFromCache(firebaseUser.uid);
        let accountInGoodStanding = forceRefresh ? null : getAccountStandingFromCache(firebaseUser.uid);


        // 🔹 **Prevent Duplicate Fetch: Check if fetch is already in progress**
         if (!userProfile && activeFetchRequestsRef.current.has(firebaseUser.uid)) {
             try {
                 // Await whatever is already in flight …
                 ({ userProfile, accountInGoodStanding } =
                     await activeFetchRequestsRef.current.get(firebaseUser.uid));
             } catch (err) {
                 // … but if it failed (eg, profile wasn’t ready yet) ignore it
                 console.warn(
                     `[AuthContext] Stale fetch for ${firebaseUser.uid} failed; retrying with fresh fetch`
                 );
             }
         }

 // Still nothing?  Launch a fresh fetch now that the profile should exist
 if (!userProfile) {
     const freshPromise = fetchUserProfile(firebaseUser.uid);
     activeFetchRequestsRef.current.set(firebaseUser.uid, freshPromise);
     ({ userProfile, accountInGoodStanding } = await freshPromise);
     activeFetchRequestsRef.current.delete(firebaseUser.uid);
 }

        // 🛑 If no profile in cache, fetch it from Firestore
        if (!userProfile) {
//            console.log(`[AuthContext] No valid cached profile found for UID: ${firebaseUser.uid}. Fetching from Firestore...`);

            // Track active fetch request
            const fetchPromise = fetchUserProfile(firebaseUser.uid);
            activeFetchRequestsRef.current.set(firebaseUser.uid, fetchPromise);

            try {
                // **Fetch full profile & account standing** from Firestore
                const fetchedData = await fetchPromise;

                // 🛑 **Edge Case: If fetchUserProfile Fails, Reset User State**
                if (!fetchedData || !fetchedData.userProfile) {
                    console.error("[AuthContext] Fetched user profile is null, resetting user.");
                    dispatch({ type: 'RESET_USER' });
                    activeFetchRequestsRef.current.delete(firebaseUser.uid); // Cleanup tracking
                    return; // Prevent setting invalid state
                }

                // **Use the freshly fetched profile & account standing**
                userProfile = fetchedData.userProfile;
                accountInGoodStanding = fetchedData.accountInGoodStanding;

//                console.log(`[AuthContext] Successfully fetched profile for UID: ${firebaseUser.uid}`, userProfile);
//                console.log(`[AuthContext] Account in good standing: ${accountInGoodStanding}`);

                // 🔹 **Ensure we store account standing in cache after fetching**
//                console.log(`[AuthContext] Storing fetched account standing in cache for UID: ${firebaseUser.uid}`);
                storeAccountStandingInCache(firebaseUser.uid, accountInGoodStanding, userProfile.subscriptionStatus);
            } catch (fetchError) {
                console.error("[AuthContext] Error during profile fetch:", fetchError.message);
                dispatch({ type: 'RESET_USER' });
                setAuthLoading(false);
                return;
            } finally {
                activeFetchRequestsRef.current.delete(firebaseUser.uid); // Cleanup tracking
            }
        } else {
//            console.log(`[AuthContext] Using cached profile for UID: ${firebaseUser.uid}`);
        }

        // 🟢 Step 2: Extract critical values (ensures valid defaults)
        const organizationId = userProfile.organizationId ?? null;
        const userPermissions = userProfile.permissions || [];
        const userRole = userProfile.role || null;
        const isOwner = userProfile.isOwner || false; 

        // 🟢 Step 2.5: Persist last login timestamp on accounts/{uid}
        //     This lets other parts of the app (like User Management) show
        //     "Last active" information in the Activity column.
        try {
            const userAccountRef = doc(db, 'accounts', firebaseUser.uid);
            await updateDoc(userAccountRef, {
                lastLoginAt: serverTimestamp(),
            });
        } catch (err) {
            console.error(
                '[AuthContext] Failed to update lastLoginAt for UID',
                firebaseUser.uid,
                ':',
                err.message || err
            );
        }

        // 🟢 Step 3: Dispatch action to update the state
//        console.log(`[AuthContext] Dispatching SET_USER with organizationId: ${organizationId}`);
        dispatch({
            type: 'SET_USER',
            payload: {
                currentUser: firebaseUser,
                userPermissions,
                userRole,
                organizationId,  
                userProfile: { 
                    firstName: userProfile.firstName || "",
                    lastName: userProfile.lastName || "",
                    avatarUrl: userProfile.avatarUrl || null,
                    email: userProfile.email || "unknown", // Ensure email is never missing
                    subscriptionStatus: userProfile.subscriptionStatus || "unknown",
                    hasValidPaymentMethod: userProfile.hasValidPaymentMethod ?? false,
                    planName: userProfile.planName || "N/A",
                    renewalDate: userProfile.renewalDate || null,
                    trialEndsAt: userProfile.trialEndsAt || null,
                    isOwner,
                    role: userRole,
                },
                accountInGoodStanding, // Include account standing
            },
        });


        // 🟢 Step 4: Log and finalize auth state update
//        console.log("[AuthContext] User state updated successfully. Setting authLoading to false.");
        setAuthLoading(false);

    } catch (error) {
        console.error("[AuthContext] Error in setAuthState:", error.message);

        // 🔎 DEBUG: Identify *why* we are resetting
        console.warn("[AuthContext][DEBUG] setAuthState -> RESET_USER due to error:", {
          error: error?.message || error,
          firebaseUserUid: firebaseUser?.uid,
          stateUid: state.currentUser?.uid,
        });

        // 🔴 Step 5: Reset auth state on error
        dispatch({ type: 'RESET_USER' });
        setAuthLoading(false); // Ensure loading is reset after failure
    }

}, [dispatch, setAuthLoading, state.currentUser]); // ✅ Memoized to prevent infinite loops



useEffect(() => {
    // Public preview/embed routes are intentionally unauthenticated runtimes:
    //  • public preview mode for screenshot/headless rendering
    //  • public embed route for iframe rendering
    //
    // ENTERPRISE:
    // Skip Firebase Auth listener setup for these routes and resolve React auth
    // context as anonymous. This keeps public embeds lightweight while leaving
    // authenticated app routes, builder routes, private forms, billing, and DAM
    // flows untouched.
    const bypassAuthRuntime = shouldBypassAuthRuntime();

    if (bypassAuthRuntime) {
//        console.log("[AuthContext] Public unauthenticated runtime detected — skipping Firebase Auth setup.");

        // This does NOT call Firebase signOut().
        // This does NOT mutate Firebase persistence.
        // It only resolves this React app runtime as anonymous.
        dispatch({ type: 'RESET_USER' });
        setInitialized(true);
        setAuthLoading(false);

        if (isMounted.current && isAuthLoadingTracked.current) {
            setLoading(false, 'Auth');
            isAuthLoadingTracked.current = false;
        }

        return;
    }


    // Ensure the listener is only initialized once
    if (authListenerInitialized.current) {
//        console.log("[AuthContext] Listener already initialized, skipping setup.");
        return;
    }

//    console.log("[AuthContext] Starting Firebase Auth state listener");

    // Mark the listener as initialized
    authListenerInitialized.current = true;

    // Only trigger START_LOADING if we aren't already in a loading state
    if (!state.loading) {
        dispatch({ type: 'START_LOADING' });
    }

    // Track loading in LoadingContext if not already tracked
    if (isMounted.current && !isAuthLoadingTracked.current) {
//        console.log("[AuthContext] Tracking 'Auth' in LoadingContext");
        setLoading(true, 'Auth'); // Register loading for 'Auth'
        isAuthLoadingTracked.current = true;
    }

    // Define the auth state change handler (NO debounce).
    // Firebase already coalesces auth transitions; debouncing here delays
    // the "logged-in" state and can cause a brief redirect-to-login flicker.
    const handleAuthStateChange = async (user) => {
      //  This fires every time Firebase finishes (re)hydrating auth state
      //     - "logged-in"  → we received a valid Firebase user object
      //     - "no user"    → Firebase explicitly told us there is **no** user
//    console.log(
//      '[AuthContext] onAuthStateChanged →',
//      user ? 'logged-in' : 'no user',
//      user?.uid ?? ''
//    );

      try {
        if (!user) {
          // Avoid unnecessary state resets
          if (state.currentUser !== null || state.loading) {
//          console.log("[AuthContext] No user detected, resetting state.");

            // 🔎 DEBUG: Prove this reset came from Firebase reporting "no user"
            console.warn("[AuthContext][DEBUG] onAuthStateChanged -> RESET_USER (firebase returned no user)", {
              prevUid: state.currentUser?.uid ?? null,
              stateLoading: state.loading,
              initialized,
              authLoading,
            });

            dispatch({ type: 'RESET_USER' });
          } else {

//          console.log("[AuthContext] User already null, skipping RESET_USER.");
          }

          // Ensure 'Auth' loading is stopped
          if (isMounted.current && isAuthLoadingTracked.current) {
            setLoading(false, 'Auth');
            isAuthLoadingTracked.current = false;
            setAuthLoading(false);
          }
        } else {
          // Avoid redundant updates
          if (state.currentUser?.uid === user.uid) {
//          console.log("[AuthContext] User already set, skipping update.");
            return;
          }

//        console.log(`[AuthContext] Detected user login: ${user.uid}, calling setAuthState`);

          // Use `setAuthState` which handles centralized fetching & caching
          await setAuthState(user);

          // Ensure 'Auth' loading is stopped
          if (isMounted.current && isAuthLoadingTracked.current) {
            setLoading(false, 'Auth');
            isAuthLoadingTracked.current = false;
            setAuthLoading(false);
          }
        }
      } catch (error) {
        console.error("[AuthContext] Error in onAuthStateChanged:", error.message);

        // Reset auth state in case of an error
        dispatch({ type: 'RESET_USER' });

        // Ensure 'Auth' loading is stopped
        if (isMounted.current && isAuthLoadingTracked.current) {
          setLoading(false, 'Auth');
          isAuthLoadingTracked.current = false;
          setAuthLoading(false);
        }
      } finally {
        // Mark initialization as complete
        if (isMounted.current && !initialized) {
//        console.log("[AuthContext] Marking initialization complete.");
          setInitialized(true);
        }
      }
    };

    // Subscribe to Firebase auth state changes
    const unsubscribe = auth.onAuthStateChanged(handleAuthStateChange);


    return () => {
//        console.log("[AuthContext] Unsubscribing from auth state listener");

        // Clean up the listener
        unsubscribe();

        if (isMounted.current && isAuthLoadingTracked.current) {
            setLoading(false, 'Auth');
            isAuthLoadingTracked.current = false;
        }

        // Reset authListenerInitialized on unmount
        authListenerInitialized.current = false;
    };
}, [setLoading, setAuthState]);

/**
 * Checks if the current user has a specific permission or a set of permissions.
 * @param {string | string[]} requiredPermissions - The required permission(s) (e.g., 'uploadAsset' or ['manageBilling', 'viewInvoices']).
 * @returns {boolean} Whether the user has the required permission(s).
 * @throws {Error} If the user is not authenticated or permissions are not set.
 */
const hasPermission = useCallback((requiredPermissions, requireOwnership = false) => {
    if (!state.currentUser || !Array.isArray(state.userPermissions)) {
        // Auth is still resolving OR user is signed out.
        // Fail closed quietly to avoid console spam during the login hydration window.
        return false;
    }

    // Normalize requiredPermissions to an array for consistent handling
    const permissionsToCheck = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];

//    console.log(`[AuthContext] Checking permissions: ${permissionsToCheck}`);
//    console.log(`[AuthContext] User permissions: ${state.userPermissions}`);

    // Check if the user has ALL required permissions
    const hasAllPermissions = permissionsToCheck.every(permission => state.userPermissions.includes(permission));

    // Ensure ownership requirement is met
    if (requireOwnership && !state.userProfile?.isOwner) {
        console.warn("[AuthContext] Ownership validation failed. User is not the owner.");
        return false;
    }

//    console.log(`[AuthContext] Has all permissions: ${hasAllPermissions}`);
    return hasAllPermissions;
}, [state.userProfile, state.userPermissions]);

/**
 * Function to get the Firebase ID token for the current user.
 * Prevents errors by ensuring state.currentUser exists before calling getIdToken().
 * @param {boolean} forceRefresh - Whether to force a new token refresh.
 * @returns {Promise<string|null>} The Firebase ID token or null if no user.
 */
const getToken = useCallback(async (forceRefresh = false) => {
    if (!state.currentUser) {
        console.error("[AuthContext] Attempted to get token but no authenticated user found.");
        return null;
    }

    try {
        return await state.currentUser.getIdToken(forceRefresh);
    } catch (error) {
        console.error("[AuthContext] Error retrieving ID token:", error.message);
        return null;
    }
}, [state.currentUser]);

/**
 * Memoized context value to prevent unnecessary re-renders.
 */
const contextValue = useMemo(() => {
//    console.log("[AuthContext] Context value memoized. Initialized:", initialized, "Current user:", state.currentUser);

    return {
        currentUser: state.currentUser,
        userPermissions: state.userPermissions,
        userRole: state.userRole,
        organizationId: state.organizationId,
        userProfile: state.userProfile,
        initialized,
        authLoading,
        accountInGoodStanding: state.accountInGoodStanding,
        setAuthState,
        hasPermission,
        getToken,
        dispatch,
    };
}, [
    initialized,
    authLoading, //  Add this so re-renders account for loading updates
    state.currentUser?.uid, //  Only update when user changes
    state.userPermissions,
    state.userRole,
    state.organizationId,
    initialized,
    authLoading,
    state.accountInGoodStanding
]);

/**
 * Cleanup on unmount and when logging out
 */
useEffect(() => {
    return () => {
//        console.log("[AuthContext] Cleaning up on unmount. Resetting authListenerInitialized.");
        authListenerInitialized.current = false; //  Ensures proper re-initialization on logout
    };
}, []);


// Return the provider with the updated context value
return (
    <AuthContext.Provider value={contextValue}>
        {children}
    </AuthContext.Provider>
);
}

export default AuthProvider;
